import { anyToString, arrayPush, UNDEFINED_TYPE } from './../js/js.utils';
import { isArray, isNil, isNotNil, isPlainObject, isString } from './../predicates/predicates';

/* Copyright IBM Corp. 2018 */
/*
 * Makes sure that a path starts and ends with slash
 *
 * @param aUrl  the url
 * @return the fixed URL
 */
function _ensureStartingAndTrailingSlash(aUrl?: string): string {
  // special cases
  if (!aUrl || (aUrl === '/')) {
    // returns the slide slash
    return '/';
  }
  // test for start and end
  const bStart = aUrl.startsWith('/');
  const bEnd = aUrl.endsWith('/');
  // handle
  return bStart ? bEnd ? aUrl : `${aUrl}/` : bEnd ? `/${aUrl}` : `/${aUrl}/`;
}

/*
 * Makes sure our path ends with a proper trailing slash
 */
function _ensureTrailingSlash(aUrl: string): string {
  return (aUrl.endsWith('/') ? aUrl : aUrl + '/');
}

/**
 * Regular expression to parse a URL according to
 *
 * https://tools.ietf.org/html/rfc3986#appendix-B
 */
const PARSE_URL = /^(((?:[^:\/?#]+):)?(?:\/\/([^\/?#]*))?)([^?#]*)(\?(?:[^#]*))?(#(?:.*))?$/;

const PARSE_HOST = /^([^:]+)(?:\:(\d+))?$/;

/** Fallback to an empty string if the value is not defined */
function _fallbackToEmpty(aValue: string | undefined): string {
  return isNotNil(aValue) ? aValue : '';
}

/**
 * Parses the URL string by applying a regular expression. Avoiding the use of the URL constructor
 * which is not available on some platforms, notably not on Angular universal.
 *
 * @param aUrl  the URL string
 * @return the URL object
 */
function _parseUrlString(aUrl: string): URL {
  // split
  const [href, origin, protocol, host, pathname, search, hash] = PARSE_URL.exec(aUrl);
  const [full, hostname, port] = PARSE_HOST.exec(host);
  return {
    href: _fallbackToEmpty(href),
    protocol: _fallbackToEmpty(protocol),
    host: _fallbackToEmpty(host),
    hostname: _fallbackToEmpty(hostname),
    port: _fallbackToEmpty(port),
    pathname: _fallbackToEmpty(pathname),
    search: _fallbackToEmpty(search),
    hash: _fallbackToEmpty(hash),
    origin: _fallbackToEmpty(origin),
    username: '',
    password: '',
    searchParams: undefined,
    toString: () => aUrl
  };
}

/*
 * Parses a URL or string into a URL object
 *
 * @param aURL  the URL
 * @return the parsed URL object
 */
function _parseURL(aURL: URL | string | null | undefined): URL | null | undefined {
  // type check
  return isString(aURL) ? _parseUrlString(aURL) : aURL;
}

/*
 * Clones the URL object into a bean
 *
 * @param aURL  the URL object
 * @return the URL bean
 */
function _cloneURL(aURL: URL): URL {
  return {
    hash: aURL.hash,
    host: aURL.host,
    hostname: aURL.hostname,
    href: aURL.href,
    origin: aURL.origin,
    password: aURL.password,
    pathname: aURL.pathname,
    port: aURL.port,
    protocol: aURL.protocol,
    search: aURL.search,
    username: aURL.username,
    searchParams: null
  };
}

/* Copyright IBM Corp. 2017 */
const encode = encodeURIComponent;
const decode = decodeURIComponent;

/*
 * Adds a key value pair to an array, so the array can later be joined via '&'
 *
 * @param aEncodedKey   the already encoded key
 * @param aValue        the value
 * @param aArray        the array to push to
 */
function _push(aEncodedKey: string, aValue: any, aArray: string[]) {
  arrayPush(aEncodedKey + '=' + encode(anyToString(aValue)), aArray);
}

/*
 * Converts various inputs into a query string.
 *
 * @param aValue the input
 * @return the result of the serialization
*/
function _buildQueryString(aValue: any): string | null | undefined {
  // test the options
  if (isString(aValue) || isNil(aValue)) {
    return aValue;
  }
  // handle the object case
  if (isPlainObject(aValue)) {
    // serialize
    const tokens: string[] = [];
    // iterate over the object
    // tslint:disable-next-line:forin
    for (const key in aValue) {
      // encoded key
      const encodedKey = encode(key);
      // get the array
      const data = aValue[key];
      if (isArray(data)) {
        // iterate over the values
        const len = data.length;
        for (let i = 0; i < len; ++i) {
          // converty any type of  object
          _push(encodedKey, data[i], tokens);
        }
      } else {
        // converty any type of  object
        _push(encodedKey, data, tokens);
      }
    }
    // convert
    return tokens.join('&');
  }
  // handle objects such as URLSearchParams
  return anyToString(aValue);
}

export interface ParsedQuery {
  [key: string]: string | string[];
}

/*
 * Correct decoding for value
 *
 * @param aValue    the value
 * @return the value
 */
function _decodeQueryArg(aValue: string): string {
  return isNotNil(aValue) ? decode(aValue.replace('+', ' ')) : aValue;
}

/*
 * Parses the query string into key/value pairs
 *
 * @param aQuery    the query string
 * @return the query result object
 */
function _parseQuery(aQuery: string): ParsedQuery {
  // the result object
  const result: ParsedQuery = {};
  // sanity check
  if (isNotNil(aQuery)) {
    // split the query
    const nvs = aQuery.split('&');
    const nvsLen = nvs.length;
    for (let i = 0; i < nvsLen; ++i) {
      // split
      let key: string;
      let value: string;
      const nv = nvs[i];
      const idx = nv.indexOf('=');
      if (idx >= 0) {
        // split key and value
        key = _decodeQueryArg(nv.substring(0, idx));
        value = _decodeQueryArg(nv.substring(idx + 1));
      } else {
        key = _decodeQueryArg(nv);
        value = undefined;
      }
      // update
      if (result.hasOwnProperty(key)) {
        // check for the type
        const oldValue = result[key];
        if (isArray(oldValue)) {
          oldValue.push(value);
        } else {
          result[key] = [oldValue, value];
        }
      } else {
        result[key] = value;
      }
    }
  }
  // ok
  return result;
}

/**
 * Extracts the base URL from the current document
 *
 * @param aDoc  the document
 * @return the URL
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/base
 */
function _getBaseUrlFromDocument(aDoc: Document): string | undefined {
  try {
    return aDoc ? _ensureTrailingSlash(aDoc.baseURI) : undefined;
  } catch (error) {
    // nothing to return
    return undefined;
  }
}

/**
* Extracts the base URL from the window
*
* @param aDoc  the window
* @return the URL
*/
function _getBaseUrlFromWindow(aWindow?: Window): string | undefined {
  // check if we have a window
  return (isNotNil(aWindow) && isNotNil(aWindow.document)) ? _getBaseUrlFromDocument(aWindow.document) : (typeof window !== UNDEFINED_TYPE) ? _getBaseUrlFromDocument(window.document) : (typeof document !== UNDEFINED_TYPE) ? _getBaseUrlFromDocument(document) : undefined;
}

/*
 * Shortcut to convert a URL to a string
 *
 * @param aURL  the URL
 * @return the resulting string
 */
function _urlToString(aURL: URL | string): string {
  return isNotNil(aURL) ? aURL.toString() : undefined;
}

function _getPathForSearch(aPath: string): string {
  // construct the search string
  const segments = aPath.split('/').filter(Boolean).map(decode);
  segments.splice(0, 0, '');
  return segments.join('/');
}

export {
  _getPathForSearch as pathForSearch,
  _getBaseUrlFromDocument as getBaseUrlFromDocument,
  _getBaseUrlFromWindow as getBaseUrlFromWindow,
  _ensureStartingAndTrailingSlash as urlSlashes,
  _buildQueryString as queryToString,
  _parseQuery as parseQueryString,
  _urlToString as urlToString,
  _cloneURL as cloneURL,
  _parseURL as parseURL
};
